import React, { useState, useMemo, useEffect } from "react";
import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { cn } from "../../utils/cn";
import FleetrifyCheckbox from "../Checkbox/checkbox";
import { FleetrifyButton } from "../Button/customButton";
import { fleetrifyIcons } from "../../assets/icons/icons";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";

export interface Column {
  label: string;
  key: keyof any;
  sortable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
}

export interface ActionMenuItem {
  label: string;
  func: () => void;
}

export interface AnimatedTableProps {
  data: any[]; // current page data from API
  columns: Column[];
  checkbox?: boolean;
  currentPage?: number;
  lastPage?: number;
  total?: number;
  totalPage?: number;
  setCurrentPage?: (param: number) => void;
  tableHeadClassName?: string;
  tableBodyClassName?: string;
  pageSize?: number;
  isLoading?: boolean;
  actionMenu?: (row: any) => ActionMenuItem[];
  onRowClick?: (row: any) => void;
  className?: string;
  hideFooter?: boolean;
  renderLeftContent?: React.ReactNode;
  tableTitle?: string;
  tableClassName?: string;
  // tableData: any[];

  // optional props to avoid TS errors
  tooltipLeft?: boolean;
  view?: boolean;
}

export const FleetrifyCustomTable: React.FC<AnimatedTableProps> = ({
  data,
  columns,
  checkbox = false,
  currentPage = 1,
  lastPage = 1,
  total,
  setCurrentPage,
  tableHeadClassName,
  tableBodyClassName,
  pageSize = 10,
  isLoading,
  actionMenu,
  onRowClick,
  className,
  hideFooter,
  renderLeftContent,
  tableTitle,
  tableClassName,
  // tableData,
}) => {
  // const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof any;
    direction: "asc" | "desc";
  } | null>(null);
  // const [selectAll, setSelectAll] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

  // SORTING (works per page)
  const sortedData = useMemo(() => {
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];
        if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  // No local slicing, backend already paginates
  const paginatedData = sortedData;

  // HANDLE SORT
  const handleSort = (key: keyof any) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // SELECTION
  // const handleRowSelect = (id: number, checked: boolean) => {
  //   setSelectedRows((prev) =>
  //     checked ? [...prev, id] : prev.filter((rowId) => rowId !== id)
  //   );
  // };

  // const handleSelectAll = (checked: boolean) => {
  //   setSelectAll(checked);
  //   setSelectedRows(checked ? paginatedData.map((item) => item.id) : []);
  // };

  // useEffect(() => {
  //   setSelectAll(
  //     paginatedData.length > 0 &&
  //     paginatedData.every((item) => selectedRows.includes(item.id))
  //   );
  // }, [selectedRows, paginatedData]);

  // HEADER
  const renderHeader = () => {
    const isDataAvailable = data?.length > 0;
    return (
      <thead className="bg-[#E7E9EE]">
        <tr>
          {/* {checkbox && (
            <th className="px-3 py-3.5">
              <div className={cn("flex items-center h-5", !isDataAvailable && "cursor-not-allowed")}>
                <FleetrifyCheckbox
                  checked={selectAll}
                  onChange={(e, checked) => handleSelectAll(checked)}
                  size="small"
                  disabled={!isDataAvailable}
                />
              </div>
            </th>
          )} */}
          {columns.map((column) => {
            const isSortable = column.sortable;
            const isActiveSort = sortConfig?.key === column.key;
            return (
              <th
                key={column.key as any}
                scope="col"
                className={cn(
                  "px-6 py-3.5 text-left text-sm font-semibold text-gray-900",
                  isSortable && (isDataAvailable ? "cursor-pointer" : "cursor-not-allowed"),
                  "transition-colors duration-200",
                  isSortable && isDataAvailable && "hover:bg-gray-100",
                  "relative",
                  tableHeadClassName
                )}
                onClick={() => isSortable && isDataAvailable ? handleSort(column.key) : undefined}
              >
                <div className="flex items-center">
                  {column.label}
                  {isSortable && (
                    <div className="ml-1.5">
                      <ArrowUpDown className={cn("h-4 w-4", isActiveSort && "opacity-100", "transition-opacity", "text-gray-500")} />
                    </div>
                  )}
                </div>
              </th>
            );
          })}
          {actionMenu && <th className="px-4 py-3.5 text-sm font-semibold text-gray-900 text-right"></th>}
        </tr>
      </thead>
    );
  };

  // BODY
  const renderBody = () => {
    const toggleDropdown = (id: number) => setOpenDropdownId((prev) => (prev === id ? null : id));
    const skeletonRows = Array.from({ length: paginatedData?.length || 10 });
    const noData = paginatedData.length === 0;

    return (
      <tbody
        className={cn(
          "bg-white divide-y divide-gray-200",
          isLoading || noData ? "cursor-not-allowed" : onRowClick ? "cursor-pointer" : ""
        )}
      >
        {isLoading
          ? skeletonRows.map((_, rowIndex) => (
              <tr key={rowIndex} className="relative">
                {checkbox && (
                  <td className="px-3 py-4 whitespace-nowrap">
                    <Skeleton width={20} height={20} borderRadius="4px" />
                  </td>
                )}
                {columns.map((_, idx) => (
                  <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-[#606060]">
                    <Skeleton width={150} height={16} />
                  </td>
                ))}
                {actionMenu && (
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <Skeleton width={32} height={32} circle />
                  </td>
                )}
              </tr>
            ))
          : noData
          ? (
            <tr>
              <td colSpan={columns.length + (checkbox ? 1 : 0) + (actionMenu ? 1 : 0)}
                  className="py-16 text-center text-sm text-gray-500">
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="text-5xl text-gray-300">📄</div>
                  <p className="text-base font-medium text-gray-600">No data available</p>
                  <p className="text-sm text-gray-400">Try adjusting your filters or adding new entries.</p>
                </div>
              </td>
            </tr>
          )
          : paginatedData.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  "relative hover:bg-[#F5F6FA]",
                  // selectedRows.includes(row.id) && "bg-[#F5F6FA]",
                  isLoading || noData ? "cursor-not-allowed" : onRowClick ? "cursor-pointer" : ""
                )}
                onClick={() => !isLoading && !noData && onRowClick?.(row)}
              >
                {/* {checkbox && (
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center h-5" onClick={(e) => e.stopPropagation()}>
                      <FleetrifyCheckbox
                        checked={selectedRows.includes(row.id)}
                        onChange={(e, checked) => handleRowSelect(row.id, checked)}
                        size="small"
                      />
                    </div>
                  </td>
                )} */}
                {columns.map((column, idx) => {
                  const rawValue = row[column.key];
                  const displayValue = column.render ? column.render(rawValue ?? "N/A", row) : rawValue ?? "N/A";
                  return (
                    <td key={idx} className={`px-6 py-4 whitespace-nowrap text-sm text-[#606060] ${tableBodyClassName}`}>
                      {displayValue}
                    </td>
                  );
                })}
                {actionMenu && (
                  <td className="relative px-4 py-4 whitespace-nowrap text-right">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleDropdown(row.id); }}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
                    >
                      {fleetrifyIcons.ellipseVerticalIcon}
                    </button>
                    <AnimatePresence>
                      {openDropdownId === row.id && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          transition={{ duration: 0.2 }}
                          className="absolute right-4 top-10 z-10 bg-white border border-gray-200 rounded shadow-md w-40"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {actionMenu(row).map((item, index) => (
                            <div
                              key={index}
                              className="px-4 py-2 text-sm text-[#1D1D1D] border-b text-left hover:bg-gray-100 cursor-pointer"
                              onClick={() => { item.func(); setOpenDropdownId(null); }}
                            >
                              {item.label}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </td>
                )}
              </tr>
            ))
        }
      </tbody>
    );
  };

  // FOOTER
  const renderFooter = () => (
    <tfoot className="bg-gray-50">
      <tr>
        <td colSpan={columns.length + (checkbox ? 1 : 0) + (actionMenu ? 1 : 0)} className="px-6 py-3 relative">
          {isLoading ? (
            <div className="flex gap-2 items-center">
              <p className="text-xs text0gray-600 ">Loading page...</p>
              <figure>{fleetrifyIcons.loaderIcon}</figure>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {(currentPage - 1) * pageSize + 1}-{(currentPage - 1) * pageSize + data.length} of {total ?? pageSize * lastPage}
              </p>
              <div className="flex items-center gap-2">
                <FleetrifyButton variant="outline" size="icon" onClick={() => setCurrentPage && setCurrentPage(1)} disabled={currentPage === 1}><ChevronsLeft className="h-4 w-4" /></FleetrifyButton>
                <FleetrifyButton variant="outline" size="icon" onClick={() => setCurrentPage && setCurrentPage(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></FleetrifyButton>
                <FleetrifyButton variant="outline" size="icon" onClick={() => setCurrentPage && setCurrentPage(currentPage + 1)} disabled={currentPage === lastPage}><ChevronRight className="h-4 w-4" /></FleetrifyButton>
                <FleetrifyButton variant="outline" size="icon" onClick={() => setCurrentPage && setCurrentPage(lastPage)} disabled={currentPage === lastPage}><ChevronsRight className="h-4 w-4" /></FleetrifyButton>
              </div>
            </div>
          )}
        </td>
      </tr>
    </tfoot>
  );

  return (
    <div className={cn("w-full", className)}>
      {tableTitle && <h2 className="pl-4 pt-6 text-[#00032A] font-bold text-base">{tableTitle}</h2>}
      {renderLeftContent && <div className="flex flex-col justify-between">{renderLeftContent}</div>}
      <div className={cn("bg-white rounded-lg shadow-md overflow-x-auto", tableClassName)}>
        <table className="min-w-full divide-y divide-gray-300">
          {renderHeader()}
          {renderBody()}
          {!hideFooter && renderFooter()}
        </table>
      </div>
    </div>
  );
};


